import { useEffect, useRef, useState } from "react";
import { ArrowDownToLine, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUpdate } from "@/contexts/UpdateContext";

/**
 * Banner fixo no topo da tela quando há uma versão nova em produção.
 * Mostra countdown de 3 minutos antes de forçar reload — dá tempo do
 * usuário terminar o que está fazendo, e os hooks useDraftAutosave
 * gravam rascunhos no localStorage antes do reload.
 *
 * O texto principal é o resumo da release (shared/release-notes.ts), servido
 * por /api/version — este bundle é o ANTIGO e não conhece a versão nova. Uma
 * interrupção forçada precisa justificar por que vale a pena: "Nova versão
 * disponível" não justifica nada, "Deixa a busca de contatos mais rápida" sim.
 *
 * Botões:
 *  - "Atualizar agora" → reload imediato.
 *  - "Adiar 3min" → soma 180s ao countdown (máximo 1x).
 */
const COUNTDOWN_SECONDS = 180;
const MAX_POSTPONES = 1;

function formatMmSs(totalSeconds: number) {
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

export function UpdateBanner() {
  const { updateAvailable, forceReload, serverVersion } = useUpdate();
  // Pode ser null: versão sem nota cadastrada, ou servidor mais antigo que
  // esta mudança (o campo nem vem na resposta). Nesse caso, o texto genérico.
  const note = serverVersion?.releaseNote ?? null;
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);
  const [postpones, setPostpones] = useState(0);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!updateAvailable) return;
    const tick = window.setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          window.clearInterval(tick);
          if (!reloadingRef.current) {
            reloadingRef.current = true;
            void forceReload();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(tick);
  }, [updateAvailable, forceReload]);

  if (!updateAvailable) return null;

  const handleReloadNow = () => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    void forceReload();
  };

  const handlePostpone = () => {
    if (postpones >= MAX_POSTPONES) return;
    setPostpones(p => p + 1);
    setRemaining(r => r + COUNTDOWN_SECONDS);
  };

  return (
    <div
      role="alert"
      className="fixed top-0 inset-x-0 z-[60] bg-primary text-primary-foreground shadow-md"
    >
      <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <ArrowDownToLine className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <div className="text-sm leading-tight min-w-0">
            <p>
              <span className="font-medium">
                {/* O resumo VEM PRIMEIRO: "nova versão disponível" não diz
                    nada a quem está no meio de um atendimento. O que retém a
                    atenção é o ganho. */}
                {note ? note.summary : "Nova versão disponível."}
              </span>{" "}
              <span className="opacity-90">
                Atualizando em <span className="tabular-nums">{formatMmSs(remaining)}</span>.
              </span>
              <span className="opacity-90 hidden sm:inline">
                {" "}Seus rascunhos serão preservados.
              </span>
            </p>
            {/* Detalhes só no desktop: no mobile a barra já compete com o
                conteúdo da tela e o resumo sozinho basta. */}
            {note?.highlights && note.highlights.length > 0 && (
              <ul className="mt-1 hidden sm:flex flex-wrap gap-x-3 gap-y-0.5 opacity-90 text-xs">
                {note.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-1">
                    <Check className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={handlePostpone}
            disabled={postpones >= MAX_POSTPONES}
            className="h-8 flex-1 sm:flex-initial"
          >
            Adiar 3min
          </Button>
          <Button
            size="sm"
            variant="default"
            onClick={handleReloadNow}
            className="h-8 bg-white text-primary hover:bg-white/90 flex-1 sm:flex-initial"
          >
            Atualizar agora
          </Button>
        </div>
      </div>
    </div>
  );
}
